/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useState, useRef, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grommet } from 'grommet'
import {
  AdvisoryActivityIcons,
  HeaderFour,
  TabButton,
  TabSet,
} from 'secondstep-components'

import { launchPresentation } from 'utils/playerHelper'
import HsIndividualResourceActivityDetails from 'components/HsIndividualResourceActivityDetails'
import HsIndividualResourceMetadata from 'components/HsIndividualResourceMetadata'
import HsIndividualResourceReferences from 'components/HsIndividualResourceReferences'
import HsIndividualResourceMarkComplete from 'components/HsIndividualResourceMarkComplete'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import HighSchoolPreviewBanner from 'components/HighSchoolPreviewBanner'
import MarkCompleteV25 from 'components/MarkCompleteV2_5'
import BackLink from 'components/BackLink'
import { getAudienceMetadata, getGradeMetadata } from 'utils/highschoolHelpers'
import {
  extractAdvisoryResourcesFromActivityDetails,
  extractGradeValue,
  extractUnitValue,
  getAdvisoryIconColorByProgram,
  getAdvisoryActivityHref,
  getPhaseSections,
  groupAdvisoryActivitiesByType,
  hasBeforeLearningExperienceSection,
  isAdvisoryActivitiesHeaderBlock,
  getBreadcrumbText,
  normalizePhaseSection,
  SECTION_PHASES,
  getBackPath,
} from './component.helpers'
import { getLanguageToggle } from 'components/HsIndividualResourceActivityDetails/helpers'
import {
  getCurrentProgram,
  PROGRAM_TYPES,
  getProgramTheme,
} from 'utils/programHelpers'
import { AppToContentGroupCode } from 'utils/productMaps'
import {
  tabTheme,
  ActivityButton,
  AudienceTagContainer,
  ControlPanel,
  DetailsContainer,
  ExternalLinkIcon,
  HeaderContainer,
  PageContainer,
  StyledHeaderOne,
  SurveyLink,
  BannerSpacer,
  SiteSelectorWrapper,
  HsIndividualResourceMarkCompleteWrapper,
  Divider,
  HeaderTitleContainer,
  TitleWrapper,
  HeaderContentWrapper,
  MarkCompleteContainer,
  MetadataContainer,
  PhaseContentWrapper,
  PhaseSectionBody,
  ResourceContainer,
  BreadcrumbWrapper,
  NavigationWrapper,
  SectionButtonRow,
  SectionContainer,
  SectionHeaderRow,
  SectionTitle,
  StyledMarkdownParagraph,
  ResourceImageContainer,
  PreviewBannerWrapper,
  AdvisoryActivitiesContainer,
  AdvisoryActivitiesTitle,
  AdvisoryActivitiesGrid,
  AdvisoryInlineLink,
  AdvisoryInlineLinkRow,
  AdvisoryInlineLinks,
  PhaseTextLinkWithFileIcon,
  WeeklyPlanContentContainer,
  WeeklyPlanHeaderContainer,
  WeeklyPlanOuterContainer,
} from './component.styles'
import {
  SURVEY_TEXT,
  LAUNCH_ACTIVITY_BUTTON_TEXT,
  LAUNCH_LESSON_BUTTON_TEXT,
  PREVIEW_BUTTON_TEXT,
} from './constants'
import externalLink from './assets/external_link.svg'
import SiteSelector from 'components/SiteSelector'
import ExperiencePlayerLaunchButton from 'components/ExperiencePlayerLaunchButton'
import { getRiseActivityLaunchInfo } from 'components/ExperiencePlayerLaunchButton/helpers'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContextLegacy'
import AdvisoryActivityLink from 'experienceComponents/AdvisoryActivityLink'
import ImageFullWidth from 'experienceComponents/ImageFullWidth'
import HeaderBodyCopy from 'experienceComponents/HeaderBodyCopy'
import ImageBodyCopyFlexible from 'components/ImageBodyCopyFlexible'
import InlineVideo from 'components/InlineVideo'
import LanguageToggle from 'experienceComponents/LanguageToggle'
import contentTypes from 'utils/contentfulParser/contents'
import { toCamelCase, toPascalCase } from 'utils/stringHelpers'
import { LanguageToggleWrapper as SharedLanguageToggleWrapper } from 'components/HsIndividualResourceActivityDetails/component.styles'

import { transformLegacyEntryForV2_5View } from './helpers'

const VIDEO_VARIANT = 'highSchool'
const CONTENT_GROUP_TO_INSTRUCTOR_KEY = {
  dwb: 'Dwb',
  k5seldigital: 'K5SelDigital',
  mspdigital: 'MspDigital',
  highschool: 'HighSchool',
}
const PROGRAM_PATH_TO_CONTENT_GROUP = {
  elementary: 'k5SelDigital',
  'middle-school': 'mspDigital',
  'high-school': 'highSchool',
}

const hasRenderablePhaseSection = section => {
  return Array.isArray(section?.content) && section.content.length > 0
}

const CONTENT_MAPPING = {
  headerBodyCopy: {
    component: HeaderBodyCopy,
    parser: (content, handlers) => ({
      ...contentTypes.headerBodyCopy(content),
      ...handlers,
    }),
  },
  h4: {
    component: HeaderFour,
    parser: content => ({
      children: content?.h4Content,
      dataTestId: content?.h4Content?.toLowerCase()?.replace(' ', '-'),
    }),
  },
  textLinkIcon: {
    component: PhaseTextLinkWithFileIcon,
    parser: contentTypes.textLinkIcon,
  },
  image: {
    component: ImageFullWidth,
    parser: content => ({
      imageThumbnailUrl: content?.image?.file?.url || content?.image?.url,
      imageUrl: content?.image?.file?.url || content?.image?.url,
      title: '',
      isZoomable: true,
    }),
  },
  imageBodyCopyFlexible: {
    component: ImageBodyCopyFlexible,
    parser: content => ({
      ...contentTypes.imageBodyCopyFlexible(content),
      isZoomable: true,
      title: '',
    }),
  },
  video: {
    component: InlineVideo,
    parser: content => ({
      entryId: content?.id,
      locale: content?.locale,
      variant: VIDEO_VARIANT,
    }),
  },
}

const renderContentComponent = ({ contentBlock, key, handlers }) => {
  const normalizedContentType =
    typeof contentBlock?.contentType === 'string'
      ? toCamelCase(contentBlock.contentType)
      : contentBlock?.contentType?.id
  const normalizedSnakeContentType =
    typeof contentBlock?.content_type === 'string'
      ? toCamelCase(contentBlock.content_type)
      : contentBlock?.content_type?.id
  const normalizedType = contentBlock?.type
    ? toCamelCase(contentBlock.type)
    : null

  const mapping =
    CONTENT_MAPPING[normalizedContentType] ||
    CONTENT_MAPPING[normalizedSnakeContentType] ||
    CONTENT_MAPPING[normalizedType] ||
    CONTENT_MAPPING[toCamelCase(contentBlock?.__typename)]

  if (!mapping) {
    return null
  }

  const ContentComponent = mapping.component
  return (
    <ContentComponent key={key} {...mapping.parser(contentBlock, handlers)} />
  )
}

const resolveAdvisoryIcon = advisoryActivity => {
  const iconNameCandidates = [
    advisoryActivity?.advisoryIcon,
    advisoryActivity?.advisoryActivityType,
  ].filter(Boolean)

  for (const iconNameCandidate of iconNameCandidates) {
    const camelCaseName = toCamelCase(iconNameCandidate)
    const singularCamelCaseName = camelCaseName.endsWith('s')
      ? camelCaseName.slice(0, -1)
      : camelCaseName

    const iconFromCandidate =
      AdvisoryActivityIcons[toPascalCase(camelCaseName)] ||
      AdvisoryActivityIcons[toPascalCase(singularCamelCaseName)]

    if (iconFromCandidate) {
      return iconFromCandidate
    }
  }

  return null
}

const isAdvisoryActivityTextLinkBlock = contentBlock => {
  const normalizedContentType =
    typeof contentBlock?.contentType === 'string'
      ? toCamelCase(contentBlock.contentType)
      : contentBlock?.contentType?.id
  const normalizedSnakeContentType =
    typeof contentBlock?.content_type === 'string'
      ? toCamelCase(contentBlock.content_type)
      : contentBlock?.content_type?.id
  const normalizedType = contentBlock?.type
    ? toCamelCase(contentBlock.type)
    : null
  const normalizedTypename = toCamelCase(contentBlock?.__typename)

  return [
    normalizedContentType,
    normalizedSnakeContentType,
    normalizedType,
    normalizedTypename,
  ]
    .filter(Boolean)
    .map(value => String(value).toLowerCase())
    .includes('textlinkicon')
}

const IndividualResourceV2_5 = ({
  entry,
  playerUserContext,
  navigationEvent,
  isHsPreview,
  isPreview,
  productTabTitle,
  match,
  routeContent,
  useLineLength880 = true,
  useSecondaryMarkCompleteButton = true,
}) => {
  // Memoize the legacy transform so it (and the downstream payload scan) only
  // recompute when the entry payload changes, rather than on every render.
  const transformedEntry = useMemo(
    () => transformLegacyEntryForV2_5View({ entry }),
    [entry],
  )
  if (!transformedEntry) return null

  const contentGroupCode =
    routeContent?.contentGroupCode ||
    AppToContentGroupCode[routeContent?.route?.app] ||
    PROGRAM_PATH_TO_CONTENT_GROUP[match?.params?.program] ||
    null
  const instructorDataKey = contentGroupCode
    ? CONTENT_GROUP_TO_INSTRUCTOR_KEY[String(contentGroupCode).toLowerCase()] ||
      null
    : null
  const configuredMarkCompleteV25 = instructorDataKey
    ? {
        instructorDataKey,
      }
    : null

  const {
    activityDetails,
    facetMetadataSetting,
    image,
    markComplete: markCompleteContent,
    presentation,
    references,
    resourceMetadata,
    surveyLink,
    languageToggle: entryLanguageToggle,
  } = transformedEntry || {}
  const { changeLocale, locale: routeLocale } = routeContent || {}
  const { metadataValues } = resourceMetadata || {}
  const [activeActivityIndex, setActiveActivityIndex] = useState(0)
  const [reference, setReference] = useState()
  const [openAccordionReference, setOpenAccordionReference] = useState(false)
  const supRef = useRef()

  const scrollToBottom = () => {
    setTimeout(() => supRef.current.scrollIntoView({ behavior: 'smooth' }), 500)
  }

  const { description, displayTitle, courseNodeConnector } =
    resourceMetadata || {}
  const { courseNodeEntryPoint: courseNodeId } = courseNodeConnector || {}
  const audience = getAudienceMetadata(resourceMetadata)
  const grades = getGradeMetadata(resourceMetadata)
  const currentProgram = getCurrentProgram()
  const isHighSchool = currentProgram === PROGRAM_TYPES.HIGH_SCHOOL
  const activityDetailsList = Array.isArray(activityDetails)
    ? activityDetails
    : []
  const hasActivityDetails = activityDetailsList.length > 0
  const activeActivityDetail =
    activityDetailsList[activeActivityIndex] || activityDetailsList[0] || null

  const resolvedPresentation =
    presentation ||
    activeActivityDetail?.presentation ||
    resourceMetadata?.presentation ||
    transformedEntry?.metadata?.presentation ||
    null
  const resolvedMarkCompleteContent =
    markCompleteContent ||
    activeActivityDetail?.markComplete ||
    resourceMetadata?.markComplete ||
    transformedEntry?.metadata?.markComplete ||
    null
  const resolvedLanguageToggle =
    getLanguageToggle(activeActivityDetail) || entryLanguageToggle || null
  const canToggleLanguage =
    typeof resolvedLanguageToggle?.boundary === 'boolean'
      ? resolvedLanguageToggle.boundary
      : !!(
          (resolvedLanguageToggle?.enUs ?? resolvedLanguageToggle?.en_us) &&
          (resolvedLanguageToggle?.esUs ?? resolvedLanguageToggle?.es_us)
        )
  const shouldShowLanguageToggle = !!(canToggleLanguage && changeLocale)

  const handleReferences = refClicked => {
    const referenceClicked = refClicked.replace(/[^\w\s]/gi, '')
    if (refClicked && referenceClicked) {
      setOpenAccordionReference(true)
      setReference(referenceClicked)
      scrollToBottom()
    }
  }

  const handleActivityTabs = activityIndex => {
    if (openAccordionReference) {
      setOpenAccordionReference(!openAccordionReference)
    }
    setReference(null)
    setActiveActivityIndex(activityIndex)
  }

  useEffect(() => {
    if (displayTitle) {
      document.title = `${productTabTitle} | ${displayTitle}`
    }
  }, [displayTitle, productTabTitle])

  const launchActivity = (isPreviewLaunch = false) => {
    if (!resolvedPresentation?.id) return
    const { id } = resolvedPresentation
    const theme = getProgramTheme()
    const mode = isPreviewLaunch ? 'preview' : ''
    const lessonTitle = resourceMetadata?.routeSlug
    const siteId = playerUserContext?.sites?.[0]?.siteId

    const baseExtraParams = isPreviewLaunch ? '&tier=2' : ''
    const extraQueryParams = baseExtraParams

    launchPresentation({
      entryId: id,
      locale: 'en-US',
      mode,
      navigationEvent,
      theme,
      lessonTitle,
      extraQueryParams,
      siteId,
      rawToken: true,
      userContext: playerUserContext,
    })
  }

  const shouldShowMarkCompleteHs = ({ markCompleteContent, isPreview }) => {
    if (isPreview) return false
    if (!markCompleteContent) return false
    if (configuredMarkCompleteV25) return false
    if (currentProgram !== PROGRAM_TYPES.HIGH_SCHOOL) return false
    return true
  }

  const showsMarkCompleteV25 = !!configuredMarkCompleteV25 && !isPreview
  const showsHsMarkComplete = shouldShowMarkCompleteHs({
    markCompleteContent: resolvedMarkCompleteContent,
    isPreview,
  })

  const gradeValue = extractGradeValue(metadataValues)
  const unitValue = extractUnitValue(metadataValues)
  const audienceTypeTag = unitValue
  const audienceGradeTag = isHighSchool ? grades : gradeValue?.toUpperCase()
  const phaseSections = getPhaseSections({
    activityDetailsList,
    activeActivityDetail,
    entry: transformedEntry,
  })
  const beforeSection = phaseSections[SECTION_PHASES.BEFORE]
  const duringSection = phaseSections[SECTION_PHASES.DURING]
  const afterSection = phaseSections[SECTION_PHASES.AFTER]
  const normalizedBeforeSection = normalizePhaseSection({
    section: beforeSection,
  })
  const normalizedDuringSection = normalizePhaseSection({
    section: duringSection,
  })
  const normalizedAfterSection = normalizePhaseSection({
    section: afterSection,
  })
  const hasBeforeSection = hasRenderablePhaseSection(normalizedBeforeSection)
  const hasDuringSection = hasRenderablePhaseSection(normalizedDuringSection)
  const hasAfterSection = hasRenderablePhaseSection(normalizedAfterSection)
  // The before preview button should only render when the payload explicitly
  // defines a LearningExperienceSection with type "before". Memoized (with the
  // memoized transformedEntry) to avoid deep-scanning the payload every render.
  const hasBeforePreviewSection = useMemo(
    () => hasBeforeLearningExperienceSection(transformedEntry),
    [transformedEntry],
  )
  const hasPhaseSections = !!(
    hasBeforeSection ||
    hasDuringSection ||
    hasAfterSection
  )
  const riseActivityLaunchInfo = getRiseActivityLaunchInfo(transformedEntry)
  const hasRisePresentation = !!riseActivityLaunchInfo?.lessonId
  const advisoryResources =
    Array.isArray(transformedEntry?.additionalResources) &&
    transformedEntry.additionalResources.length
      ? transformedEntry.additionalResources
      : extractAdvisoryResourcesFromActivityDetails({
          activityDetailsList,
        })

  const advisoryActivityGroups = groupAdvisoryActivitiesByType(
    advisoryResources,
  )
  const flattenedAdvisoryActivities = Object.values(
    advisoryActivityGroups,
  ).flat()
  const showAdvisoryActivities = Object.keys(advisoryActivityGroups).length > 0
  const advisoryIconColor = getAdvisoryIconColorByProgram(currentProgram)
  const advisoryActivitiesHeaderIndex = useMemo(() => {
    if (!showAdvisoryActivities) return -1
    return (normalizedAfterSection?.content || []).findIndex(contentBlock =>
      isAdvisoryActivitiesHeaderBlock(contentBlock),
    )
  }, [showAdvisoryActivities, normalizedAfterSection])
  const breadcrumbText = getBreadcrumbText(currentProgram, productTabTitle)

  return (
    <PageContainer className="individual-resource-print-page">
      {isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <HighSchoolPreviewBanner />
        </PreviewBannerWrapper>
      )}
      {isPreview && !isHsPreview && (
        <PreviewBannerWrapper>
          <BannerSpacer />
          <CoursePreviewBanner />
        </PreviewBannerWrapper>
      )}

      {!isPreview && (
        <NavigationWrapper data-print-hidden="navigation">
          {!isHighSchool && (
            <BreadcrumbWrapper>
              <BackLink
                dataTestId="breadcrumb"
                onClick={() =>
                  navigationEvent(
                    getBackPath(
                      window.location.pathname,
                      window.location.search,
                    ),
                  )
                }
              >
                {breadcrumbText}
              </BackLink>
            </BreadcrumbWrapper>
          )}
          <SiteSelectorWrapper isHighSchool={isHighSchool}>
            <SiteSelector />
          </SiteSelectorWrapper>
        </NavigationWrapper>
      )}
      <HeaderContainer>
        <div className="content">
          <HeaderContentWrapper isHighSchool={isHighSchool}>
            <AudienceTagContainer
              audience={audience}
              isHighSchool={isHighSchool}
            >
              {!!audienceTypeTag && <div>{audienceTypeTag}</div>}
              {!!audienceGradeTag && <div>{audienceGradeTag}</div>}
            </AudienceTagContainer>
            <HeaderTitleContainer>
              <TitleWrapper>
                <StyledHeaderOne>{displayTitle}</StyledHeaderOne>
              </TitleWrapper>
              {!hasPhaseSections && showsMarkCompleteV25 && (
                <div data-print-hidden="mark-done">
                  <MarkCompleteV25
                    courseNodeId={courseNodeId}
                    instructorDataKey={
                      configuredMarkCompleteV25.instructorDataKey
                    }
                    useSecondaryMarkCompleteButton={
                      useSecondaryMarkCompleteButton
                    }
                  />
                </div>
              )}
            </HeaderTitleContainer>
            <StyledMarkdownParagraph
              $lineLength880={useLineLength880}
              isHighSchool={isHighSchool}
              small
            >
              {description}
            </StyledMarkdownParagraph>
            {!!surveyLink && (
              <SurveyLink
                href={surveyLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                {SURVEY_TEXT}
                <ExternalLinkIcon
                  alt={'External Link Icon'}
                  src={externalLink}
                />
              </SurveyLink>
            )}
            {!hasPhaseSections && showsHsMarkComplete && (
              <HsIndividualResourceMarkCompleteWrapper data-print-hidden="mark-done">
                <HsIndividualResourceMarkComplete
                  activityType={audience}
                  courseNodeId={courseNodeId}
                  isV3
                  markCompleteContent={resolvedMarkCompleteContent}
                  practiceName={displayTitle}
                />
              </HsIndividualResourceMarkCompleteWrapper>
            )}
          </HeaderContentWrapper>
          {isHighSchool && !!image?.url && (
            <ResourceImageContainer
              alt={image?.description}
              className="resource-image"
              src={image?.url}
            />
          )}
        </div>
      </HeaderContainer>
      <ResourceContainer isHighSchool={isHighSchool}>
        <MetadataContainer isHighSchool={isHighSchool}>
          <HsIndividualResourceMetadata
            audience={audience}
            disableOuterBackground
            facetMetadataSetting={facetMetadataSetting}
            program={currentProgram}
            resourceMetadata={resourceMetadata}
          />
        </MetadataContainer>
        {isHighSchool && !hasPhaseSections && (
          <ControlPanel>
            <div className="content">
              {hasActivityDetails && (
                <Grommet theme={tabTheme}>
                  <TabSet>
                    {activityDetailsList.map((activity, index) => (
                      <TabButton
                        dataTestId={`${activity.displayTitle
                          .toLowerCase()
                          .replace(' ', '-')}-tab`}
                        isActive={index === activeActivityIndex}
                        key={`${activity.displayTitle.toLowerCase()}-${index}`}
                        label={activity.displayTitle}
                        onClick={() => handleActivityTabs(index)}
                      />
                    ))}
                  </TabSet>
                </Grommet>
              )}
              {!!resolvedPresentation && !isPreview && (
                <ActivityButton
                  $isHighSchoolButton={isHighSchool}
                  onClick={() => launchActivity(isPreview)}
                >
                  {LAUNCH_ACTIVITY_BUTTON_TEXT}
                </ActivityButton>
              )}
            </div>
          </ControlPanel>
        )}
        <DetailsContainer audience={audience} isHighSchool={isHighSchool}>
          <div className="content">
            {hasPhaseSections ? (
              <WeeklyPlanOuterContainer
                audience={audience}
                isHighSchool={isHighSchool}
              >
                {!isHighSchool && (
                  <WeeklyPlanHeaderContainer>
                    Lesson Plan
                  </WeeklyPlanHeaderContainer>
                )}
                <WeeklyPlanContentContainer>
                  <PhaseContentWrapper>
                    {hasBeforeSection && (
                      <SectionContainer>
                        {shouldShowLanguageToggle && (
                          <SectionButtonRow
                            $alignLeft
                            $compact
                            data-print-hidden="before-language-toggle-row"
                          >
                            <SharedLanguageToggleWrapper
                              data-print-hidden="language-toggle"
                              locale={routeLocale}
                            >
                              <LanguageToggle
                                boundary={canToggleLanguage}
                                getEntryWithNewLocale={changeLocale}
                                locale={routeLocale}
                              />
                            </SharedLanguageToggleWrapper>
                          </SectionButtonRow>
                        )}
                        <SectionHeaderRow>
                          <SectionTitle>
                            {normalizedBeforeSection?.displayTitle}
                          </SectionTitle>
                          {hasBeforePreviewSection &&
                            (!!resolvedPresentation || hasRisePresentation) && (
                              <SectionButtonRow
                                $alignLeft
                                data-print-hidden="before-controls-row"
                              >
                                {hasRisePresentation ? (
                                  <ExperiencePlayerLaunchButton
                                    dataTestId="preview-experience-button"
                                    expiration={
                                      riseActivityLaunchInfo.expiration
                                    }
                                    forceFixedWidth
                                    isHighSchool={isHighSchool}
                                    label={PREVIEW_BUTTON_TEXT}
                                    lessonId={riseActivityLaunchInfo.lessonId}
                                  />
                                ) : (
                                  <ActivityButton
                                    $isHighSchoolButton
                                    onClick={() => launchActivity(true)}
                                  >
                                    {PREVIEW_BUTTON_TEXT}
                                  </ActivityButton>
                                )}
                              </SectionButtonRow>
                            )}
                        </SectionHeaderRow>
                        <PhaseSectionBody
                          $lineLength880={useLineLength880}
                          isHighSchool={isHighSchool}
                        >
                          {(normalizedBeforeSection?.content || []).map(
                            (content, index) =>
                              renderContentComponent({
                                contentBlock: content,
                                key: `before-${index}`,
                                handlers: {
                                  referenceHandler: handleReferences,
                                  presentation: resolvedPresentation,
                                  launchActivity,
                                  isPreview,
                                },
                              }),
                          )}
                        </PhaseSectionBody>
                      </SectionContainer>
                    )}

                    {hasDuringSection && (
                      <>
                        {hasBeforeSection && <Divider />}
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedDuringSection?.displayTitle}
                            </SectionTitle>
                            <SectionButtonRow
                              $alignTop
                              data-print-hidden="during-controls-row"
                            >
                              {hasRisePresentation && !isPreview && (
                                <ExperiencePlayerLaunchButton
                                  dataTestId="launch-experience-button"
                                  expiration={riseActivityLaunchInfo.expiration}
                                  isHighSchool={isHighSchool}
                                  label={
                                    isHighSchool
                                      ? LAUNCH_ACTIVITY_BUTTON_TEXT
                                      : LAUNCH_LESSON_BUTTON_TEXT
                                  }
                                  lessonId={riseActivityLaunchInfo.lessonId}
                                />
                              )}
                              {!!resolvedPresentation &&
                                !isPreview &&
                                !hasRisePresentation && (
                                  <ActivityButton
                                    $isHighSchoolButton={isHighSchool}
                                    onClick={() => launchActivity(false)}
                                  >
                                    {isHighSchool
                                      ? LAUNCH_ACTIVITY_BUTTON_TEXT
                                      : LAUNCH_LESSON_BUTTON_TEXT}
                                  </ActivityButton>
                                )}
                              {showsMarkCompleteV25 && (
                                <MarkCompleteContainer
                                  $isHighSchool={isHighSchool}
                                  data-print-hidden="mark-done"
                                >
                                  <MarkCompleteV25
                                    courseNodeId={courseNodeId}
                                    instructorDataKey={
                                      configuredMarkCompleteV25.instructorDataKey
                                    }
                                    useSecondaryMarkCompleteButton={
                                      useSecondaryMarkCompleteButton
                                    }
                                  />
                                </MarkCompleteContainer>
                              )}
                              {isHighSchool && showsHsMarkComplete && (
                                <MarkCompleteContainer
                                  $isHighSchool={isHighSchool}
                                  data-print-hidden="mark-done"
                                >
                                  <HsIndividualResourceMarkComplete
                                    activityType={audience}
                                    courseNodeId={courseNodeId}
                                    isInline
                                    isV3
                                    markCompleteContent={
                                      resolvedMarkCompleteContent
                                    }
                                    practiceName={displayTitle}
                                  />
                                </MarkCompleteContainer>
                              )}
                            </SectionButtonRow>
                          </SectionHeaderRow>
                          <PhaseSectionBody
                            $lineLength880={useLineLength880}
                            isHighSchool={isHighSchool}
                          >
                            {(normalizedDuringSection?.content || []).map(
                              (content, index) =>
                                renderContentComponent({
                                  contentBlock: content,
                                  key: `during-${index}`,
                                  handlers: {
                                    referenceHandler: handleReferences,
                                    presentation: resolvedPresentation,
                                    launchActivity,
                                    isPreview,
                                  },
                                }),
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}

                    {hasAfterSection && (
                      <>
                        {(hasBeforeSection || hasDuringSection) && <Divider />}
                        <SectionContainer>
                          <SectionHeaderRow>
                            <SectionTitle>
                              {normalizedAfterSection?.displayTitle}
                            </SectionTitle>
                          </SectionHeaderRow>
                          <PhaseSectionBody
                            $lineLength880={useLineLength880}
                            isHighSchool={isHighSchool}
                          >
                            {(normalizedAfterSection?.content || []).map(
                              (content, index) => {
                                const shouldHideDuplicatedAdvisoryLink =
                                  advisoryActivitiesHeaderIndex > -1 &&
                                  index > advisoryActivitiesHeaderIndex &&
                                  isAdvisoryActivityTextLinkBlock(content)
                                return (
                                  <React.Fragment
                                    key={`after-fragment-${index}`}
                                  >
                                    {!shouldHideDuplicatedAdvisoryLink &&
                                      renderContentComponent({
                                        contentBlock: content,
                                        key: `after-${index}`,
                                        handlers: {
                                          referenceHandler: handleReferences,
                                          presentation: resolvedPresentation,
                                          launchActivity,
                                          isPreview,
                                        },
                                      })}
                                    {showAdvisoryActivities &&
                                      isAdvisoryActivitiesHeaderBlock(
                                        content,
                                      ) && (
                                        <AdvisoryInlineLinks>
                                          {flattenedAdvisoryActivities.map(
                                            activity => {
                                              const advisoryHref = getAdvisoryActivityHref(
                                                { activity },
                                              )
                                              if (!advisoryHref) {
                                                return null
                                              }

                                              const AdvisoryIcon = resolveAdvisoryIcon(
                                                activity,
                                              )

                                              return (
                                                <AdvisoryInlineLinkRow
                                                  iconColor={advisoryIconColor}
                                                  key={activity.id}
                                                >
                                                  {!!AdvisoryIcon && (
                                                    <span className="advisory-inline-icon">
                                                      <AdvisoryIcon />
                                                    </span>
                                                  )}
                                                  <AdvisoryInlineLink
                                                    href={advisoryHref}
                                                  >
                                                    {activity.displayTitle}
                                                  </AdvisoryInlineLink>
                                                </AdvisoryInlineLinkRow>
                                              )
                                            },
                                          )}
                                        </AdvisoryInlineLinks>
                                      )}
                                  </React.Fragment>
                                )
                              },
                            )}
                          </PhaseSectionBody>
                        </SectionContainer>
                      </>
                    )}
                  </PhaseContentWrapper>
                </WeeklyPlanContentContainer>
              </WeeklyPlanOuterContainer>
            ) : (
              !!activityDetailsList[activeActivityIndex] && (
                <HsIndividualResourceActivityDetails
                  audience={audience}
                  details={activityDetailsList[activeActivityIndex]}
                  getEntryWithNewLocale={changeLocale}
                  isPreview={isPreview}
                  languageToggle={
                    getLanguageToggle(
                      activityDetailsList[activeActivityIndex],
                    ) ||
                    entryLanguageToggle ||
                    null
                  }
                  launchActivity={launchActivity}
                  launchButtonText={LAUNCH_LESSON_BUTTON_TEXT}
                  locale={routeLocale}
                  presentation={resolvedPresentation}
                  program={currentProgram}
                  referenceHandler={handleReferences}
                />
              )
            )}
            {!hasPhaseSections && showAdvisoryActivities && (
              <AdvisoryActivitiesContainer>
                <AdvisoryActivitiesTitle>
                  Advisory Activity Links
                </AdvisoryActivitiesTitle>
                <AdvisoryActivitiesGrid>
                  {Object.keys(advisoryActivityGroups).map(advisoryType => (
                    <AdvisoryActivityLink
                      advisoryType={advisoryActivityGroups[advisoryType]}
                      key={advisoryType}
                      productName={match?.params?.program || 'middle-school'}
                    />
                  ))}
                </AdvisoryActivitiesGrid>
              </AdvisoryActivitiesContainer>
            )}
            {!!references && (
              <HsIndividualResourceReferences
                audience={audience}
                focusedReference={reference}
                isOpen={openAccordionReference}
                ref={supRef}
                references={references}
                setAccordionCollapse={setOpenAccordionReference}
              />
            )}
          </div>
        </DetailsContainer>
      </ResourceContainer>
    </PageContainer>
  )
}

IndividualResourceV2_5.propTypes = {
  entry: PropTypes.object,
  isHsPreview: PropTypes.bool,
  isPreview: PropTypes.bool,
  match: PropTypes.func,
  navigationEvent: PropTypes.func,
  playerUserContext: PropTypes.object,
  productTabTitle: PropTypes.string,
  routeContent: PropTypes.object,
  useLineLength880: PropTypes.bool,
  useSecondaryMarkCompleteButton: PropTypes.bool,
}

export default withSiteContext(IndividualResourceV2_5, {
  useGuard: true,
})
